import React, { useState, useEffect, useMemo, useCallback } from "react";
import { type NodeProps, type Node, useReactFlow } from "@xyflow/react";
import {
  Database,
  X,
  FileText,
  Link,
  Type,
  ChevronDown,
  ChevronRight,
  Search,
  RefreshCw,
  Loader2,
  Check,
  FolderOpen,
  Folder,
  Globe,
} from "lucide-react";
import { WorkflowBaseNode } from "../../common/shared/WorkflowBaseNode";
import { WorkflowNodeModal } from "../../common/shared/WorkflowNodeModal";
import syntaxHighlight from "../../utils/jsonViewer";
import documentStoresService, {
  FolderItem,
  FileProcessTypeEnums,
  AiPerformanceLevelEnums,
} from "../../../services/document-stores.service";

// ─── Types ────────────────────────────────────────────────────────────────────

interface DocumentStore {
  _id: string;
  id: string;
  folder_name: string;
  document_store_type?: string;
}

interface FolderTreeNode {
  folder: FolderItem;
  children: FolderTreeNode[];
}

// ─── Folder tree helpers ──────────────────────────────────────────────────────
function buildFolderTree(folders: FolderItem[]): FolderTreeNode[] {
  const byParent = new Map<string | null, FolderItem[]>();
  for (const f of folders) {
    const key = f.parent_folder_id ?? null;
    const list = byParent.get(key) ?? [];
    list.push(f);
    byParent.set(key, list);
  }
  for (const list of byParent.values())
    list.sort((a, b) => a.name.localeCompare(b.name));
  const build = (parentId: string | null): FolderTreeNode[] =>
    (byParent.get(parentId) ?? []).map((f) => ({
      folder: f,
      children: build(f._id),
    }));
  return build(null);
}

function findBreadcrumb(folders: FolderItem[], folderId: string): string {
  const byId = new Map(folders.map((f) => [f._id, f]));
  const path: string[] = [];
  let cursor: string | null = folderId;
  let safety = 0;
  while (cursor && safety++ < 32) {
    const cur = byId.get(cursor);
    if (!cur) break;
    path.unshift(cur.name);
    cursor = cur.parent_folder_id;
  }
  return path.join(" / ");
}

// ─── Recursive folder tree renderer (single-select) ──────────────────────────
const FolderTreeList: React.FC<{
  tree: FolderTreeNode[];
  selectedId: string;
  expandedIds: Set<string>;
  onToggleExpand: (id: string) => void;
  onSelect: (id: string) => void;
  searchTerm: string;
  depth?: number;
}> = ({
  tree,
  selectedId,
  expandedIds,
  onToggleExpand,
  onSelect,
  searchTerm,
  depth = 0,
}) => {
  const q = searchTerm.trim().toLowerCase();

  const branchHasMatch = (node: FolderTreeNode): boolean => {
    if (!q) return true;
    if (node.folder.name.toLowerCase().includes(q)) return true;
    return node.children.some(branchHasMatch);
  };

  return (
    <>
      {tree.filter(branchHasMatch).map((node) => {
        const hasChildren = node.children.length > 0;
        const isExpanded = q ? true : expandedIds.has(node.folder._id);
        const isSelected = selectedId === node.folder._id;

        return (
          <div key={node.folder._id}>
            <div
              onClick={() => onSelect(node.folder._id)}
              className={`flex items-center gap-1 px-2 py-1.5 rounded-md cursor-pointer transition-colors ${
                isSelected
                  ? "bg-indigo-100 text-indigo-800"
                  : "hover:bg-gray-100 text-gray-700"
              }`}
              style={{ paddingLeft: `${depth * 14 + 8}px` }}
            >
              {/* expand/collapse toggle */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (hasChildren) onToggleExpand(node.folder._id);
                }}
                className={`w-4 h-4 flex items-center justify-center shrink-0 ${
                  hasChildren
                    ? "text-gray-400 hover:text-gray-600"
                    : "text-transparent cursor-default"
                }`}
              >
                {hasChildren &&
                  (isExpanded ? (
                    <ChevronDown className="w-3.5 h-3.5" />
                  ) : (
                    <ChevronRight className="w-3.5 h-3.5" />
                  ))}
              </button>

              {isExpanded && hasChildren ? (
                <FolderOpen
                  className={`w-3.5 h-3.5 shrink-0 ${isSelected ? "text-indigo-600" : "text-amber-500"}`}
                />
              ) : (
                <Folder
                  className={`w-3.5 h-3.5 shrink-0 ${isSelected ? "text-indigo-600" : "text-amber-500"}`}
                />
              )}

              <span
                className="truncate text-xs flex-1"
                title={node.folder.name}
              >
                {node.folder.name}
              </span>

              {isSelected && (
                <Check className="w-3 h-3 text-indigo-600 shrink-0" />
              )}
            </div>

            {isExpanded && hasChildren && (
              <FolderTreeList
                tree={node.children}
                selectedId={selectedId}
                expandedIds={expandedIds}
                onToggleExpand={onToggleExpand}
                onSelect={onSelect}
                searchTerm={searchTerm}
                depth={depth + 1}
              />
            )}
          </div>
        );
      })}
    </>
  );
};

// ─── Store + folder picker ────────────────────────────────────────────────────
const StoreAndFolderPicker: React.FC<{
  selectedStoreId: string;
  selectedFolderId: string;
  onSelectStore: (id: string) => void;
  onSelectFolder: (id: string) => void;
}> = ({ selectedStoreId, selectedFolderId, onSelectStore, onSelectFolder }) => {
  const [stores, setStores] = useState<DocumentStore[]>([]);
  const [loadingStores, setLoadingStores] = useState(false);
  const [storeSearch, setStoreSearch] = useState("");

  const [folders, setFolders] = useState<FolderItem[]>([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [folderSearch, setFolderSearch] = useState("");
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // ── fetch stores ──────────────────────────────────────────────────────
  const fetchStores = useCallback(async () => {
    setLoadingStores(true);
    try {
      const res = await documentStoresService.getDocumentStoresDropdown();
      if (res.success) setStores(res.data || []);
    } catch (e) {
      console.error("Failed to fetch document stores:", e);
    } finally {
      setLoadingStores(false);
    }
  }, []);

  useEffect(() => {
    fetchStores();
  }, [fetchStores]);

  // ── fetch folders when store changes ─────────────────────────────────
  const fetchFolders = useCallback(async (storeId: string) => {
    if (!storeId) {
      setFolders([]);
      return;
    }
    setLoadingFolders(true);
    try {
      const list = await documentStoresService.listAllFoldersForStore(storeId);
      setFolders(list || []);
      setExpandedIds(new Set());
    } catch (e) {
      console.error("Failed to fetch folders:", e);
      setFolders([]);
    } finally {
      setLoadingFolders(false);
    }
  }, []);

  useEffect(() => {
    fetchFolders(selectedStoreId);
  }, [selectedStoreId, fetchFolders]);

  const handleSelectStore = (id: string) => {
    onSelectStore(id);
    onSelectFolder(""); // clear folder when store changes
    setFolderSearch("");
  };

  const handleSelectFolder = (id: string) => {
    // clicking selected folder again deselects it
    onSelectFolder(selectedFolderId === id ? "" : id);
  };

  const filteredStores = useMemo(
    () =>
      stores.filter((s) =>
        s.folder_name.toLowerCase().includes(storeSearch.toLowerCase()),
      ),
    [stores, storeSearch],
  );

  const selectedStore = stores.find((s) => s.id === selectedStoreId);
  const folderBreadcrumb = selectedFolderId
    ? findBreadcrumb(folders, selectedFolderId)
    : "";
  const folderTree = useMemo(() => buildFolderTree(folders), [folders]);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Two-column layout */}
      <div className="flex divide-x divide-gray-200 h-72">
        {/* LEFT — Store list */}
        <div className="w-56 shrink-0 flex flex-col bg-gray-50">
          <div className="px-3 py-2.5 border-b border-gray-200 bg-white">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                Document Store
              </span>
              <button
                type="button"
                onClick={fetchStores}
                className="text-[10px] text-indigo-600 hover:text-indigo-700 flex items-center gap-0.5"
              >
                <RefreshCw
                  className={`w-3 h-3 ${loadingStores ? "animate-spin" : ""}`}
                />
                Reload
              </button>
            </div>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
              <input
                type="text"
                placeholder="Search…"
                value={storeSearch}
                onChange={(e) => setStoreSearch(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                className="w-full pl-6 pr-2 py-1 text-xs border border-gray-200 rounded bg-gray-50 focus:outline-none focus:ring-1 focus:ring-indigo-400"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-1.5">
            {loadingStores ? (
              <div className="flex items-center justify-center py-8 text-gray-400">
                <Loader2 className="w-4 h-4 animate-spin mr-1" />
                <span className="text-xs">Loading…</span>
              </div>
            ) : filteredStores.length === 0 ? (
              <p className="text-center text-gray-400 py-8 text-xs italic">
                No stores found
              </p>
            ) : (
              <div className="space-y-0.5">
                {filteredStores.map((store) => {
                  const isSel = selectedStoreId === store.id;
                  return (
                    <button
                      key={store.id}
                      type="button"
                      onClick={() => handleSelectStore(store.id)}
                      className={`w-full text-left px-2.5 py-2 rounded-md border transition-all flex items-center gap-2 ${
                        isSel
                          ? "bg-indigo-600 border-indigo-600 text-white shadow-sm"
                          : "bg-white border-transparent text-gray-700 hover:bg-indigo-50 hover:border-indigo-100"
                      }`}
                    >
                      <FolderOpen
                        className={`w-3.5 h-3.5 shrink-0 ${isSel ? "text-white/80" : "text-emerald-500"}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium truncate">
                          {store.folder_name}
                        </div>
                        {store.document_store_type && (
                          <div className={`text-[10px] truncate ${isSel ? "text-indigo-200" : "text-gray-400"}`}>
                            {store.document_store_type}
                          </div>
                        )}
                      </div>
                      {isSel && (
                        <Check className="w-4 h-4 text-white shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Selected store indicator footer */}
          <div className={`px-3 py-2 border-t transition-colors ${selectedStoreId ? "border-indigo-200 bg-indigo-600" : "border-gray-200 bg-amber-50"}`}>
            {selectedStoreId ? (
              <div className="flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5 text-white shrink-0" />
                <p className="text-[11px] font-medium text-white truncate">
                  {selectedStore?.folder_name ?? selectedStoreId}
                </p>
              </div>
            ) : (
              <p className="text-[10px] text-amber-700">
                Select a store to continue
              </p>
            )}
          </div>
        </div>

        {/* RIGHT — Folder tree */}
        <div className="flex-1 flex flex-col bg-white">
          <div className="px-3 py-2.5 border-b border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                Target Folder
                <span className="normal-case font-normal text-gray-400 ml-1">
                  (optional)
                </span>
              </span>
              {selectedStoreId && (
                <button
                  type="button"
                  onClick={() => fetchFolders(selectedStoreId)}
                  className="text-[10px] text-indigo-600 hover:text-indigo-700 flex items-center gap-0.5"
                >
                  <RefreshCw
                    className={`w-3 h-3 ${loadingFolders ? "animate-spin" : ""}`}
                  />
                  Reload
                </button>
              )}
            </div>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
              <input
                type="text"
                placeholder="Search folders…"
                value={folderSearch}
                onChange={(e) => setFolderSearch(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                disabled={!selectedStoreId}
                className="w-full pl-6 pr-2 py-1 text-xs border border-gray-200 rounded bg-gray-50 focus:outline-none focus:ring-1 focus:ring-indigo-400 disabled:opacity-40 disabled:cursor-not-allowed"
              />
            </div>
          </div>

          {/* Selected folder breadcrumb */}
          {selectedFolderId && folderBreadcrumb && (
            <div className="px-3 py-2 border-b border-gray-100 bg-indigo-50 flex items-center gap-2">
              <FolderOpen className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
              <span className="text-xs text-indigo-800 truncate flex-1">
                {folderBreadcrumb}
              </span>
              <button
                type="button"
                onClick={() => onSelectFolder("")}
                className="text-indigo-400 hover:text-indigo-600"
                title="Clear folder"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          <div className="flex-1 overflow-y-auto p-2">
            {!selectedStoreId ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2 py-8">
                <Database className="w-6 h-6" />
                <p className="text-xs italic text-center">
                  Select a document store first
                </p>
              </div>
            ) : loadingFolders ? (
              <div className="flex items-center justify-center py-8 text-gray-400">
                <Loader2 className="w-4 h-4 animate-spin mr-1" />
                <span className="text-xs">Loading folders…</span>
              </div>
            ) : folders.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2 py-8">
                <Folder className="w-6 h-6" />
                <p className="text-xs italic text-center">
                  No folders — files will be uploaded to the store root
                </p>
              </div>
            ) : (
              <FolderTreeList
                tree={folderTree}
                selectedId={selectedFolderId}
                expandedIds={expandedIds}
                onToggleExpand={(fid) =>
                  setExpandedIds((prev) => {
                    const next = new Set(prev);
                    if (next.has(fid)) next.delete(fid);
                    else next.add(fid);
                    return next;
                  })
                }
                onSelect={handleSelectFolder}
                searchTerm={folderSearch}
              />
            )}
          </div>

          {/* hint footer */}
          <div className="px-3 py-2 border-t border-gray-100 bg-gray-50/60">
            <p className="text-[10px] text-gray-500">
              Click a folder to select it as the upload destination. Click again
              to deselect (upload to store root).
            </p>
          </div>
        </div>
      </div>

      {!selectedStoreId && (
        <div className="px-3 py-2 bg-red-50 border-t border-red-100">
          <p className="text-xs text-red-600">Document store is required</p>
        </div>
      )}
    </div>
  );
};

// ─── Drag-drop variable field ─────────────────────────────────────────────────
const VariableDropField: React.FC<{
  label: string;
  required?: boolean;
  value: string;
  placeholder: string;
  hint: string;
  onDrop: (val: string) => void;
  onClear: () => void;
}> = ({ label, required, value, placeholder, hint, onDrop, onClear }) => {
  const [dragging, setDragging] = useState(false);
  const [showInput, setShowInput] = useState(false);
  const [inputVal, setInputVal] = useState("");

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const v = e.dataTransfer.getData("text/plain");
    if (v) onDrop(v);
    setDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only reset when the cursor actually leaves this element, not a child
    if (!e.currentTarget.contains(e.relatedTarget as globalThis.Node)) {
      setDragging(false);
    }
  };

  const commitInput = () => {
    if (inputVal.trim()) {
      onDrop(inputVal.trim());
      setInputVal("");
    }
    setShowInput(false);
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">
        {label} {required && <span className="text-red-500">*</span>}
      </label>

      <div
        onDrop={handleDrop}
        onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDragging(true); }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`min-h-[80px] px-4 py-3 border-2 border-dashed rounded-lg text-sm transition-all ${
          dragging
            ? "border-indigo-500 bg-indigo-50 shadow-inner"
            : value
              ? "border-indigo-300 bg-indigo-50/50"
              : "border-gray-300 bg-gray-50 hover:border-gray-400"
        }`}
      >
        {value ? (
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide pointer-events-none">
                Bound variable
              </span>
              <code className="block text-xs font-mono break-all mt-0.5 text-indigo-700 pointer-events-none">
                {value}
              </code>
            </div>
            <button
              onClick={onClear}
              className="text-gray-400 hover:text-gray-600 p-1 hover:bg-white rounded transition-colors shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-1 pointer-events-none">
            <span className="text-sm text-gray-400">{placeholder}</span>
            <span className="text-[11px] text-gray-400">{hint}</span>
          </div>
        )}
      </div>

      {/* Type / paste fallback */}
      {!value && (
        <div className="mt-1.5">
          {showInput ? (
            <div className="flex gap-1.5">
              <input
                autoFocus
                type="text"
                value={inputVal}
                onChange={(e) => setInputVal(e.target.value)}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === "Enter") commitInput();
                  if (e.key === "Escape") setShowInput(false);
                }}
                placeholder="e.g. {{Node.outputs.field}}"
                className="flex-1 px-2.5 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-indigo-400 focus:border-indigo-400 font-mono"
              />
              <button
                onClick={commitInput}
                className="px-2.5 py-1.5 text-xs bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
              >
                Set
              </button>
              <button
                onClick={() => setShowInput(false)}
                className="px-2.5 py-1.5 text-xs text-gray-500 border border-gray-200 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowInput(true)}
              className="text-[11px] text-indigo-500 hover:text-indigo-700 hover:underline"
            >
              Or type a variable path manually
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Main node ────────────────────────────────────────────────────────────────
export const DocxiqExportNode: React.FC<NodeProps<Node<any>>> = ({
  id,
  data,
  selected,
}) => {
  const { setNodes } = useReactFlow();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeInputTab, setActiveInputTab] = useState<1 | 2 | 3 | 4 | 5>(1);

  const [documentStoreId, setDocumentStoreId] = useState<string>(
    data?.document_store_id || "",
  );
  const [folderId, setFolderId] = useState<string>(data?.folder_id || "");
  const [fileObject, setFileObject] = useState<string>(
    data?.file_object || "",
  );
  // NEW: WebScraper native modes
  const [discoveredFiles, setDiscoveredFiles] = useState<string>(
    data?.discovered_files || "",
  );
  const [scrapedPages, setScrapedPages] = useState<string>(
    data?.scraped_pages || "",
  );
  const [fileUrl, setFileUrl] = useState<string>(data?.file_url || "");
  const [textContent, setTextContent] = useState<string>(
    data?.text_content || "",
  );
  const [fileUrlFileName, setFileUrlFileName] = useState<string>(
    data?.file_url_file_name || "",
  );
  const [textContentFileName, setTextContentFileName] = useState<string>(
    data?.text_content_file_name || "",
  );
  const [mimeType, setMimeType] = useState<string>(data?.mime_type || "");
  const [aiPerformanceLevel, setAiPerformanceLevel] =
    useState<AiPerformanceLevelEnums>(
      data?.ai_performance_level || AiPerformanceLevelEnums.MEDIUM,
    );
  const [fileProcessingType, setFileProcessingType] =
    useState<FileProcessTypeEnums>(
      data?.file_processing_type || FileProcessTypeEnums.AI_OCR,
    );

  // ── Validation ────────────────────────────────────────────────────────
  const validationStatus = useMemo(() => {
    const errors: string[] = [];
    if (!documentStoreId) errors.push("Document store is required");
    if (!fileObject && !discoveredFiles && !scrapedPages && !fileUrl && !textContent)
      errors.push("At least one input is required");
    return { isValid: errors.length === 0, errors };
  }, [documentStoreId, fileObject, discoveredFiles, scrapedPages, fileUrl, textContent]);

  // ── Sync to ReactFlow ────────────────────────────────────────────────
  useEffect(() => {
    setNodes((nds) =>
      nds.map((n) => {
        if (n.id !== id) return n;
        return {
          ...n,
          data: {
            ...n.data,
            input_mode: undefined, // clear stale field from old node data
            document_store_id: documentStoreId,
            folder_id: folderId || null,
            file_object: fileObject || null,
            discovered_files: discoveredFiles || null,
            scraped_pages: scrapedPages || null,
            file_url: fileUrl || null,
            text_content: textContent || null,
            file_name: fileUrlFileName || textContentFileName || null,
            file_url_file_name: fileUrlFileName || null,
            text_content_file_name: textContentFileName || null,
            mime_type: mimeType || null,
            ai_performance_level: aiPerformanceLevel,
            file_processing_type: fileProcessingType,
          },
        };
      }),
    );
  }, [
    id,
    setNodes,
    documentStoreId,
    folderId,
    fileObject,
    discoveredFiles,
    scrapedPages,
    fileUrl,
    textContent,
    fileUrlFileName,
    textContentFileName,
    mimeType,
    aiPerformanceLevel,
    fileProcessingType,
  ]);

  // ── Execution outputs ────────────────────────────────────────────────
  const hasExecutionOutputs =
    data?.outputs && Object.keys(data.outputs).length > 0;
  const highlightedOutputs = useMemo(() => {
    if (hasExecutionOutputs)
      return syntaxHighlight(JSON.stringify(data.outputs, null, 2));
    return "";
  }, [data?.outputs, hasExecutionOutputs]);


  return (
    <WorkflowBaseNode
      id={id}
      data={data}
      selected={selected}
      title="DocIQ Export"
      icon={<Database className="w-4 h-4 text-indigo-600" />}
      nodeDocsType="DOCXIQ-EXPORT"
      themeColor="indigo"
      configureLabel="Configure Export"
      isConfigured={validationStatus.isValid}
      validationErrors={validationStatus.errors}
      hasSourceHandle
      statusText={{
        running: "Uploading...",
        success: "Upload Complete",
        error: "Upload Failed",
      }}
      onConfigure={() => setIsModalOpen(true)}
    >
      {/* Inline summary */}
      {(documentStoreId || fileObject || discoveredFiles || scrapedPages || fileUrl || textContent) && (
        <div className="p-2.5 bg-white/80 border border-indigo-200 rounded-lg text-xs space-y-1.5 shadow-inner">
          {documentStoreId && (
            <div className="flex items-start gap-1.5">
              <span className="text-gray-500 shrink-0">Store:</span>
              <code className="text-indigo-700 font-mono truncate">
                {documentStoreId}
              </code>
            </div>
          )}
          <div className="flex items-center gap-1.5 flex-wrap">
            {fileObject && (
              <span className="text-indigo-700 bg-indigo-100 px-2 py-0.5 rounded text-[11px] font-medium">
                File Object
              </span>
            )}
            {discoveredFiles && (
              <span className="text-indigo-700 bg-indigo-100 px-2 py-0.5 rounded text-[11px] font-medium">
                Discovered Files
              </span>
            )}
            {scrapedPages && (
              <span className="text-indigo-700 bg-indigo-100 px-2 py-0.5 rounded text-[11px] font-medium">
                Scraped Pages
              </span>
            )}
            {fileUrl && (
              <span className="text-indigo-700 bg-indigo-100 px-2 py-0.5 rounded text-[11px] font-medium">
                File URL
              </span>
            )}
            {textContent && (
              <span className="text-indigo-700 bg-indigo-100 px-2 py-0.5 rounded text-[11px] font-medium">
                Text Content
              </span>
            )}
          </div>
        </div>
      )}

      {/* Modal */}
      <WorkflowNodeModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="DocIQ Export — Upload to Document Store"
        icon={<Database className="w-5 h-5 text-indigo-600" />}
        themeColor="indigo"
        nodeDocsType="DOCXIQ-EXPORT"
        nodeId={id}
        validationStatus={validationStatus}
        saveDisabled={!validationStatus.isValid}
        saveLabel="Save Settings"
        nodeData={data}
        nodeIcon={<Database className="w-6 h-6 text-gray-400" />}
        emptyOutputsText="No execution data"
        renderOutputsSidebar={() => (
          <div className="w-80 bg-gray-50 overflow-y-auto p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Database className="w-4 h-4 text-indigo-600" />
                <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wide">
                  Output
                </h3>
              </div>
              {hasExecutionOutputs && (
                <span className="text-[10px] font-medium px-2 py-0.5 rounded-full text-green-700 bg-green-100">
                  {`${Object.keys(data.outputs).length} fields`}
                </span>
              )}
            </div>
            {hasExecutionOutputs ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 font-mono text-xs overflow-x-auto">
                <pre
                  className="whitespace-pre-wrap break-words"
                  dangerouslySetInnerHTML={{ __html: highlightedOutputs }}
                />
              </div>
            ) : (
              <div className="p-4 bg-white border-2 border-dashed border-gray-300 rounded-lg text-center">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Database className="w-5 h-5 text-gray-400" />
                </div>
                <p className="text-xs font-medium text-gray-600">
                  No execution data
                </p>
                <p className="text-[10px] text-gray-400 mt-1">
                  Run the workflow to see outputs
                </p>
              </div>
            )}
          </div>
        )}
      >
        <div className="space-y-7">

          {/* ── Section 1: Destination ── */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
                <span className="text-[10px] font-bold text-indigo-600">1</span>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800 leading-none">Destination</p>
                <p className="text-[11px] text-gray-400 mt-0.5">Choose the document store and optional folder</p>
              </div>
            </div>
            <StoreAndFolderPicker
              selectedStoreId={documentStoreId}
              selectedFolderId={folderId}
              onSelectStore={setDocumentStoreId}
              onSelectFolder={setFolderId}
            />
          </div>

          {/* ── Section 2: Input Mode ── */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
                <span className="text-[10px] font-bold text-indigo-600">2</span>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800 leading-none">Input Mode</p>
                <p className="text-[11px] text-gray-400 mt-0.5">Fill one or more — all active modes are processed together</p>
              </div>
            </div>

            {/* Pill tab switcher */}
            <div className="flex flex-wrap gap-2 mb-4">
              {(
                [
                  { tab: 1, icon: <FileText className="w-3.5 h-3.5" />, label: "File Object", hasValue: !!fileObject },
                  { tab: 2, icon: <Globe className="w-3.5 h-3.5" />, label: "Discovered Files", hasValue: !!discoveredFiles },
                  { tab: 3, icon: <Globe className="w-3.5 h-3.5" />, label: "Scraped Pages", hasValue: !!scrapedPages },
                  { tab: 4, icon: <Link className="w-3.5 h-3.5" />, label: "File URL", hasValue: !!fileUrl },
                  { tab: 5, icon: <Type className="w-3.5 h-3.5" />, label: "Text Content", hasValue: !!textContent },
                ] as const
              ).map(({ tab, icon, label, hasValue }) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setActiveInputTab(tab)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                    activeInputTab === tab
                      ? "bg-indigo-600 text-white shadow-sm"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  {icon}
                  {label}
                  {hasValue && (
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${activeInputTab === tab ? "bg-white/70" : "bg-indigo-500"}`} />
                  )}
                </button>
              ))}
            </div>

            {/* Tab panel */}
            <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-4">
              {activeInputTab === 1 && (
                <div className="space-y-3">
                  <p className="text-[11px] text-gray-500">
                    Accepts a DMS file objects array — shape:{" "}
                    <code className="font-mono bg-white border border-gray-200 px-1 py-0.5 rounded text-[10px]">
                      {"{ url, fileName, file_type?, file_size?, key?, file_id? }[]"}
                    </code>
                  </p>
                  <VariableDropField
                    label="file_object"
                    value={fileObject}
                    placeholder="Drag file_object array from a previous node"
                    hint="{ url, fileName, file_type?, file_size?, key?, file_id? }[]"
                    onDrop={setFileObject}
                    onClear={() => setFileObject("")}
                  />
                </div>
              )}

              {activeInputTab === 2 && (
                <div className="space-y-3">
                  <p className="text-[11px] text-gray-500">
                    Directly accepts{" "}
                    <code className="font-mono bg-white border border-gray-200 px-1 py-0.5 rounded text-[10px]">
                      WebScraperNode.outputs.discovered_files
                    </code>{" "}
                    — uploads all discovered PDF/file URLs at once.
                  </p>
                  <VariableDropField
                    label="discovered_files"
                    value={discoveredFiles}
                    placeholder="Drag discovered_files array from WebScraper node"
                    hint="{ source_url, file_name, file_type }[]"
                    onDrop={setDiscoveredFiles}
                    onClear={() => setDiscoveredFiles("")}
                  />
                </div>
              )}

              {activeInputTab === 3 && (
                <div className="space-y-3">
                  <p className="text-[11px] text-gray-500">
                    Directly accepts{" "}
                    <code className="font-mono bg-white border border-gray-200 px-1 py-0.5 rounded text-[10px]">
                      WebScraperNode.outputs.scraped_pages
                    </code>{" "}
                    — each page's text becomes a{" "}
                    <code className="font-mono bg-white border border-gray-200 px-1 py-0.5 rounded text-[10px]">.txt</code>{" "}
                    file named after <code className="font-mono bg-white border border-gray-200 px-1 py-0.5 rounded text-[10px]">page_title</code>.
                  </p>
                  <VariableDropField
                    label="scraped_pages"
                    value={scrapedPages}
                    placeholder="Drag scraped_pages array from WebScraper node"
                    hint="{ page_full_text, page_title, source_url }[]"
                    onDrop={setScrapedPages}
                    onClear={() => setScrapedPages("")}
                  />
                </div>
              )}

              {activeInputTab === 4 && (
                <div className="space-y-3">
                  <p className="text-[11px] text-gray-500">
                    Accepts a single URL string or an array of URLs. Filename is optional — falls back to the name extracted from the URL.
                  </p>
                  <VariableDropField
                    label="file_url"
                    value={fileUrl}
                    placeholder="Drag or bind a file URL (string or string[])"
                    hint="e.g. {{HttpRequest.outputs.response.file_url}}"
                    onDrop={setFileUrl}
                    onClear={() => setFileUrl("")}
                  />
                  <VariableDropField
                    label="file_url_file_name (optional)"
                    value={fileUrlFileName}
                    placeholder="Drag or bind a filename (string or string[])"
                    hint="Falls back to filename from URL if omitted"
                    onDrop={setFileUrlFileName}
                    onClear={() => setFileUrlFileName("")}
                  />
                </div>
              )}

              {activeInputTab === 5 && (
                <div className="space-y-3">
                  <p className="text-[11px] text-gray-500">
                    Accepts a string or array of strings — each saved as a{" "}
                    <code className="font-mono bg-white border border-gray-200 px-1 py-0.5 rounded text-[10px]">.txt</code>{" "}
                    file. Filename defaults to{" "}
                    <code className="font-mono bg-white border border-gray-200 px-1 py-0.5 rounded text-[10px]">"content.txt"</code>.
                  </p>
                  <VariableDropField
                    label="text_content"
                    value={textContent}
                    placeholder="Drag text content from a previous node (string or string[])"
                    hint="e.g. {{ForEach.outputs.current_item.page_full_text}}"
                    onDrop={setTextContent}
                    onClear={() => setTextContent("")}
                  />
                  <VariableDropField
                    label="text_content_file_name (optional)"
                    value={textContentFileName}
                    placeholder="Drag or bind a filename (string or string[])"
                    hint='Defaults to "content.txt" if omitted'
                    onDrop={setTextContentFileName}
                    onClear={() => setTextContentFileName("")}
                  />
                </div>
              )}
            </div>
          </div>

          {/* ── Section 3: Processing Options ── */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
                <span className="text-[10px] font-bold text-indigo-600">3</span>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800 leading-none">Processing Options</p>
                <p className="text-[11px] text-gray-400 mt-0.5">Optional overrides — defaults work for most cases</p>
              </div>
            </div>

            {/* mime_type full width */}
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                MIME Type <span className="text-gray-400 font-normal">(optional — auto-detected if blank)</span>
              </label>
              <input
                type="text"
                value={mimeType}
                onChange={(e) => setMimeType(e.target.value)}
                placeholder="e.g. application/pdf"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            {/* AI level + OCR type side by side */}
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-gray-200 bg-white p-3">
                <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  AI Performance
                </label>
                <div className="relative">
                  <select
                    value={aiPerformanceLevel}
                    onChange={(e) => setAiPerformanceLevel(e.target.value as AiPerformanceLevelEnums)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm appearance-none bg-gray-50 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value={AiPerformanceLevelEnums.LOW}>LOW</option>
                    <option value={AiPerformanceLevelEnums.MEDIUM}>MEDIUM</option>
                    <option value={AiPerformanceLevelEnums.HIGH}>HIGH</option>
                  </select>
                  <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                </div>
              </div>

              <div className="rounded-lg border border-gray-200 bg-white p-3">
                <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  OCR Type
                </label>
                <div className="relative">
                  <select
                    value={fileProcessingType}
                    onChange={(e) => setFileProcessingType(e.target.value as FileProcessTypeEnums)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm appearance-none bg-gray-50 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value={FileProcessTypeEnums.AI_OCR}>AI_OCR</option>
                    <option value={FileProcessTypeEnums.NORMAL_OCR}>NORMAL_OCR</option>
                  </select>
                  <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                </div>
              </div>
            </div>
          </div>

        </div>
      </WorkflowNodeModal>
    </WorkflowBaseNode>
  );
};

export default DocxiqExportNode;
